import { useEffect, useState } from 'react';
import { Briefcase, Mail, Phone, Sparkles, Users } from 'lucide-react';
import { useNavigate } from 'react-router';
import AuthContainer from '../../components/AuthContainer';
import Header from '../../components/Header';
import { useRegisterMutation } from '../../store/api/apiSlice';
import { parseErrorMessage } from '../../utils/parseErrorMessage';
import { captureReferralFromUrl, getStoredReferralCode } from '../../utils/referral';
import type { RegisterRequest } from '../../types';
import { UserRole } from '../../types';

type SignupRole = UserRole.EMPLOYER | UserRole.HR;

export default function SignUp() {
    const navigate = useNavigate();
    const [register, { isLoading, error }] = useRegisterMutation();

    const [role, setRole] = useState<SignupRole>(UserRole.EMPLOYER);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        countryCode: '+234',
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    // Company name is hidden until the user confirms they're signing up for a
    // company/organisation — keeps the form short for individual sign-ups.
    const [isCompany, setIsCompany] = useState(false);
    const [formError, setFormError] = useState('');
    const [referralCode, setReferralCode] = useState<string | null>(null);

    // Capture ?ref=HR-XXXX on landing and persist for the rest of the signup flow.
    useEffect(() => {
        const fromUrl = captureReferralFromUrl(window.location.search);
        setReferralCode(fromUrl || getStoredReferralCode());
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setFormError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if (!acceptedTerms) {
            setFormError('Please accept the Terms of Service and Privacy Policy');
            return;
        }

        const nameParts = formData.fullName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';

        if (!firstName) {
            setFormError('Please provide your full name');
            return;
        }

        const phoneNumberWithCountryCode = formData.phoneNumber.startsWith('+')
            ? formData.phoneNumber
            : `${formData.countryCode}${formData.phoneNumber.replace(/^0+/, '')}`;

        const registerData: RegisterRequest = {
            email: formData.email,
            firstName,
            lastName,
            phoneNumber: phoneNumberWithCountryCode,
            role,
            ...(formData.companyName && { companyName: formData.companyName }),
            // Server only honours referralCode when role === 'employer'; invalid/expired codes are silently dropped.
            ...(role === UserRole.EMPLOYER && referralCode ? { referralCode } : {}),
        };
        localStorage.setItem('pending_verification_phone', phoneNumberWithCountryCode);

        try {
            await register(registerData).unwrap();
            navigate('/verify-account', { state: { phoneNumber: phoneNumberWithCountryCode } });
        } catch (err: any) {
            const rawMessage = err?.data?.message || err?.message || '';
            const errorMessage = parseErrorMessage(err, 'Registration failed. Please try again.');

            if (
                (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('verify your phone number')) ||
                errorMessage.toLowerCase().includes('verify your phone number')
            ) {
                localStorage.setItem('pending_verification_phone', phoneNumberWithCountryCode);
                navigate('/verify-account', { state: { phoneNumber: phoneNumberWithCountryCode } });
                return;
            }

            setFormError(errorMessage);
        }
    };

    const isHr = role === UserRole.HR;
    const isEmployer = role === UserRole.EMPLOYER;

    return (
        <div className="min-h-screen bg-[#FFFFFF] flex flex-col">
            <Header />
            <AuthContainer>
                <div className="w-full">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-gray-900 text-start text-[32px] md:text-[40px] font-bold mb-2">Sign Up</h1>
                        <p className="text-gray-500 text-start text-[14px] md:text-[15px]">Let's get you started!</p>
                        <p className="text-gray-600 text-start text-[14px] md:text-[15px]">
                            {isHr
                                ? 'Sign up as an HR/Partner to refer employers and earn a commission on every paid investigation.'
                                : 'Provide a few details to create your account'}
                        </p>
                    </div>

                    {/* Role Selector */}
                    <div className="mb-5 flex border-b border-gray-200">
                        <button
                            type="button"
                            onClick={() => {
                                setRole(UserRole.EMPLOYER);
                                setFormError('');
                            }}
                            className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium border-b-2 transition-colors ${
                                isEmployer
                                    ? 'border-black text-gray-900'
                                    : 'border-transparent text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            Employer
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setRole(UserRole.HR);
                                setFormError('');
                            }}
                            className={`flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium border-b-2 transition-colors ${
                                isHr
                                    ? 'border-black text-gray-900'
                                    : 'border-transparent text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            HR/Partner
                        </button>
                    </div>

                    {/* Referral banner — only meaningful for employers */}
                    {isEmployer && referralCode && (
                        <div className="mb-5 flex items-start gap-3 rounded-xl border border-orange-100 bg-orange-50 p-3">
                            <div className="text-[13px] text-orange-900">
                                Referral code <span className="font-semibold">{referralCode}</span> applied. You'll see
                                your discount on the pricing page after signing in.
                            </div>
                        </div>
                    )}

                    {/* HR partner perks card */}
                    {isHr && (
                        <div className="mb-5 rounded-xl border border-gray-100 bg-[#f5f5f5] p-4">
                            <p className="text-[13px] font-semibold text-gray-900 mb-1">What HR/Partners get</p>
                            <ul className="space-y-1 text-[12px] text-gray-600">
                                <li>• A personal referral link to share with employers</li>
                                <li>• 10% commission on every paid investigation</li>
                                <li>• Live pipeline, earnings dashboard and leaderboard</li>
                            </ul>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Error Message */}
                        {(formError || error) && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {formError || parseErrorMessage(error, 'An error occurred. Please try again.')}
                            </div>
                        )}

                        {/* Full Name */}
                        <div>
                            <label
                                htmlFor="fullName"
                                className="block text-start text-gray-900 text-[13px] font-medium mb-2"
                            >
                                {isHr ? 'Full Name' : 'Full Name / Contact Person'}
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                    required
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-start text-gray-900 text-[13px] font-medium mb-2"
                            >
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="john.doe@example.com"
                                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                    required
                                />
                            </div>
                        </div>

                        {/* Company toggle — reveals the optional company/organisation field */}
                        <label className="flex items-center gap-2.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={isCompany}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsCompany(checked);
                                    // Drop any typed value when the field is hidden again so it
                                    // isn't silently submitted.
                                    if (!checked) setFormData((prev) => ({ ...prev, companyName: '' }));
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500 accent-orange-500"
                            />
                            <span className="text-gray-700 text-[13px]">
                                {isHr
                                    ? 'I belong to an organisation'
                                    : "I'm signing up on behalf of a company"}
                            </span>
                        </label>

                        {/* Company Name — only shown once the toggle is ticked */}
                        {isCompany && (
                            <div>
                                <label
                                    htmlFor="companyName"
                                    className="block text-start text-gray-900 text-[13px] font-medium mb-2"
                                >
                                    {isHr ? 'Organisation' : 'Company Name'}
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        id="companyName"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder={isHr ? 'Where you work' : 'Your Company Ltd'}
                                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Phone Number with Country Code */}
                        <div>
                            <label
                                htmlFor="phoneNumber"
                                className="block text-start text-gray-900 text-[13px] font-medium mb-2"
                            >
                                Phone Number
                            </label>
                            <div className="flex gap-2">
                                <div className="flex items-center gap-1.5 w-24 px-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-[14px] text-gray-900">
                                    <span className="text-base leading-none">🇳🇬</span>
                                    <span>+234</span>
                                </div>
                                <div className="relative flex-1">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="8147330326"
                                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-[14px] placeholder-gray-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Terms Checkbox */}
                        <div className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300"
                                required
                            />
                            <label htmlFor="terms" className="text-gray-600 text-[13px]">
                                Accept{' '}
                                <a
                                    href="https://help.podus.org/terms?product=yourchecka"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-orange-500 hover:underline"
                                >
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                    href="https://help.podus.org/privacy?product=yourchecka"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-orange-500 hover:underline"
                                >
                                    Privacy Policy
                                </a>
                            </label>
                        </div>

                        {/* Sign Up Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-black text-white py-3 rounded-lg text-[15px] font-medium hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Creating Account...' : isHr ? 'Become an HR Partner' : 'Sign Up'}
                        </button>

                        {/* Login Link */}
                        <p className="text-center text-gray-600 text-[13px]">
                            Already have an account?{' '}
                            <a href="/login" className="text-orange-500 hover:underline font-medium">
                                Log In
                            </a>
                        </p>
                    </form>
                </div>
            </AuthContainer>
        </div>
    );
}
